import { Journey } from 'src/common/interfaces/journey.interface';
import { SubscriptionTypes } from 'src/dashboard/settings/billing';

const MAX_JOURNEY_LIMIT_FOR_STARTER_PLAN = 2;

export interface JourneyAccessInfo {
  accessibleJourneys: Journey[];
  restrictedJourneys: Journey[];
  hasRestrictedJourneys: boolean;
}

/**
 * Determines which journeys are accessible for a user based on their plan and journey count.
 * For Start plan users with more than 2 journeys, only the first 2 are accessible.
 * Delete operations are always allowed for all journeys.
 */
export const getJourneyAccessInfo = (
  journeys: Journey[],
  subscriptionPlanType: SubscriptionTypes | null,
  isOnTrial: boolean = false
): JourneyAccessInfo => {
  // If user is on trial or has unlimited journeys (Grow/Scale), all journeys are accessible
  if (isOnTrial || subscriptionPlanType === 'pro' || subscriptionPlanType === 'enterprise') {
    return {
      accessibleJourneys: journeys,
      restrictedJourneys: [],
      hasRestrictedJourneys: false,
    };
  }

  // For Start plan users, check if they have more journeys than allowed
  if (subscriptionPlanType === 'starter' && journeys.length > MAX_JOURNEY_LIMIT_FOR_STARTER_PLAN) {
    const accessibleJourneys = journeys.slice(0, MAX_JOURNEY_LIMIT_FOR_STARTER_PLAN);
    const restrictedJourneys = journeys.slice(MAX_JOURNEY_LIMIT_FOR_STARTER_PLAN);
    
    return {
      accessibleJourneys,
      restrictedJourneys,
      hasRestrictedJourneys: true,
    };
  }

  // For free plan or other cases, all journeys are accessible
  return {
    accessibleJourneys: journeys,
    restrictedJourneys: [],
    hasRestrictedJourneys: false,
  };
};

/**
 * Checks if a specific journey is accessible for operations (excluding delete).
 */
export const isJourneyAccessible = (
  journey: Journey,
  journeys: Journey[],
  subscriptionPlanType: SubscriptionTypes | null,
  isOnTrial: boolean = false
): boolean => {
  const { accessibleJourneys } = getJourneyAccessInfo(journeys, subscriptionPlanType, isOnTrial);
  return accessibleJourneys.some(j => j.uuid === journey.uuid);
};

/**
 * Gets the journey limit for the current plan.
 */
export const getJourneyLimit = (
  subscriptionPlanType: SubscriptionTypes | null,
  isOnTrial: boolean = false
): number => {
  if (isOnTrial || subscriptionPlanType === 'pro' || subscriptionPlanType === 'enterprise') {
    return Infinity; // Unlimited
  }
  
  if (subscriptionPlanType === 'starter') {
    return MAX_JOURNEY_LIMIT_FOR_STARTER_PLAN;
  }
  
  return 2; // Default for free plan
};